/**
 * @file UserAccessModal.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-10-12
 *
 * 1.1.0 (2026-10-12): Xero connection — Default (follow the system),
 * Custom Connection, or Own Xero login (users.xero_connection; the server's
 * rule is utils/xeroConnectionChoice.js). Admins only; greyed for a Rep badge,
 * before the migration, and (Custom Connection) when it is not configured,
 * each with the reason. The Custom Connection's state and today's allowance
 * sit beside it, with the plain warning that everyone on it shares 1,000
 * calls a day. Saved last, after the roles it depends on.
 * 1.1.1: a Rep badge over a stored Custom Connection / own-login setting
 * blocks the save only for someone who can change it, or when it is being
 * changed — the server puts it back to Default when the roles make a rep, so
 * a users.manage holder who is not an admin is never trapped.
 *
 * Everything about one user's access in one place: the roles they hold, the
 * sales rep they are scoped to if any, and the per-permission exceptions
 * layered on top.
 *
 * The order of the sections is the order the model resolves in — roles first,
 * then the rep link that scopes them, then the exceptions — and the badge at
 * the top updates live as roles are ticked, because the badge is the thing that
 * decides view-only mode and Xero eligibility and it should never be a surprise
 * discovered after saving.
 *
 * Saving is three separate calls (rep, roles, overrides) rather than one,
 * because the server enforces an ordering between them: a REP-badge role cannot
 * be assigned to a user with no rep, and a rep cannot be unlinked while such a
 * role is held. Sending the rep link first, then roles, then overrides means the
 * valid sequences work and the invalid ones fail with the server's own message
 * rather than a generic one.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import SearchableDropdown from './SearchableDropdown';
import PermissionPicker, { INHERIT, GRANT, REVOKE } from './PermissionPicker';
import { useSlideOutButton } from './SlideOutButton';

const BADGE_NOTE = {
  ADMIN: 'Admins hold every permission, present and future. The exceptions below have no effect while this badge is held.',
  REP: 'Rep accounts see only their own sales rep’s customers, are view-only everywhere except the Blend Calculator, and can neither connect to Xero nor be prompted to.',
  USER: 'Standard staff access. Holding several User roles adds their permissions together and leaves the badge as User.'
};

const XERO_ROUTE_TEXT = {
  custom: 'the Custom Connection',
  user: 'their own Xero login'
};

/** "812 of 1,000 calls left today (as at 14:05)", or why there is no figure. */
function allowanceText(budget) {
  if (!budget || budget.dayRemaining === null || budget.dayRemaining === undefined) {
    return 'No reading of today’s allowance yet — one is taken from each call it makes.';
  }
  const at = budget.observedAt
    ? new Date(budget.observedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })
    : null;
  const day = budget.observedAt
    ? new Date(budget.observedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Europe/London' })
    : null;
  const limit = Number(budget.dayLimit || 1000).toLocaleString('en-GB');
  return `${Number(budget.dayRemaining).toLocaleString('en-GB')} of ${limit} calls left`
    + (at ? ` at the last reading (${day} ${at})` : '')
    + (budget.callsLast24h ? ` — ${Number(budget.callsLast24h).toLocaleString('en-GB')} made in the last 24 hours.` : '.');
}

/**
 * The three choices, each with what it means for THIS person under the
 * system's current mode, and why it cannot be picked when it cannot.
 */
function xeroChoices({ xero, badge, canChange, saving }) {
  const authMode = xero?.authMode || 'user';
  const defaultRoute = authMode === 'user' ? 'user' : 'custom';
  const groupBlock = saving ? 'Saving…'
    : !xero ? 'The Xero connection settings could not be loaded.'
      : !xero.ready ? 'Not available until migrations/user_xero_connection.sql has been run.'
        : !canChange ? 'Only an administrator can change a person’s Xero connection.'
          : null;
  // Default stays open to a Rep, so one set to something else can be put back.
  const repBlock = badge === 'REP' ? 'Rep accounts never use Xero, so only Default can be saved for them.' : null;
  return [
    {
      key: 'default',
      label: 'Default',
      blurb: `Follows the system setting — at the moment that is ${XERO_ROUTE_TEXT[defaultRoute]}. Nothing changes for them until the system setting does.`,
      block: groupBlock
    },
    {
      key: 'custom',
      label: 'Custom Connection',
      blurb: 'Their changes go to Xero through the system’s own connection, noted in Xero’s History as “by <their name> via Excel Agri System”. They need no Xero login and are never asked to connect.',
      block: groupBlock || repBlock
        || (xero.ccConfigured ? null : 'The Custom Connection is not configured on this server (XERO_CC_CLIENT_ID / XERO_CC_CLIENT_SECRET).')
    },
    {
      key: 'own',
      label: 'Own Xero login',
      blurb: authMode === 'user'
        ? 'Always their own Xero login, even if the whole system later moves to the Custom Connection. The same as Default for now.'
        : 'Always their own Xero login, even though the system uses the Custom Connection. They must keep it connected — the system will ask them to.',
      block: groupBlock || repBlock
    }
  ];
}

function XeroConnectionField({ user, xero, badge, choice, setChoice, saving }) {
  const canChange = xero?.canChange === true;
  const options = xeroChoices({ xero, badge, canChange, saving });
  const budget = xero?.budget || null;
  const low = budget && (budget.level === 'warn' || budget.level === 'error');
  const others = Math.max(0, (xero?.customUsers || 0) - (user.xero_route === 'custom' ? 1 : 0));
  // The one reason that greys every choice, or a Rep's, said once under them.
  const note = options[0].block || (badge === 'REP' ? options[2].block : null);

  return (
    <div className="field">
      <span className="field-label">
        Xero connection
        {user.xero_route_label && <span className="field-count">now: {user.xero_route_label}</span>}
      </span>
      <p className="field-note">
        Which Xero identity this person’s own work goes out on — invoices pushed,
        edits, voids, contact changes. Background syncs are not affected.
      </p>
      <div className="badge-options xero-connection-options">
        {options.map(opt => (
          <button
            type="button"
            key={opt.key}
            className={`badge-option${choice === opt.key ? ' selected' : ''}`}
            disabled={!!opt.block}
            title={opt.block || undefined}
            onClick={() => setChoice(opt.key)}
          >
            <span className="xero-connection-name">{opt.label}</span>
            <span className="badge-option-blurb">{opt.blurb}</span>
          </button>
        ))}
      </div>
      {note && !saving && <p className="field-note xero-connection-block">{note}</p>}

      <div className={`xero-cc-status${low ? ' low' : ''}`}>
        <span className="material-symbols-rounded">{xero?.ccConfigured ? 'hub' : 'link_off'}</span>
        <div>
          <p>
            <strong>Custom Connection: {xero?.ccConfigured ? 'configured' : 'not configured on this server'}.</strong>
            {' '}{xero?.ccConfigured && allowanceText(budget)}
            {' '}{others === 0
              ? 'Nobody else uses it at the moment.'
              : `${others} other ${others === 1 ? 'person uses' : 'people use'} it at the moment.`}
          </p>
          <p className="xero-cc-warning">
            Everyone on the Custom Connection shares one allowance of 1,000 Xero calls a day, which
            Xero will not raise. When it runs out, their changes are not sent to Xero until the next
            day. Keep anyone who invoices in bulk on their own login.
          </p>
        </div>
      </div>
    </div>
  );
}

function badgeFrom(selectedRoles) {
  if (selectedRoles.some(r => r.badge === 'ADMIN')) return 'ADMIN';
  if (selectedRoles.some(r => r.badge === 'REP')) return 'REP';
  return 'USER';
}

function UserAccessBody({
  user, roles, categories, permissions, reps,
  selectedRoleIds, setSelectedRoleIds,
  repId, setRepId,
  overrides, setOverrides,
  xero, xeroConnection, setXeroConnection,
  saving
}) {
  useSlideOutButton([saving]);

  const selectedRoles = useMemo(
    () => roles.filter(r => selectedRoleIds.includes(r.id)),
    [roles, selectedRoleIds]
  );

  const badge = badgeFrom(selectedRoles);
  const needsRep = badge === 'REP';

  // What the chosen roles grant between them, and which role to name as the
  // source when a permission is inherited. First role wins the attribution —
  // "from Standard User" is more useful than "from 3 roles".
  const { roleGrants, grantSource } = useMemo(() => {
    const grants = new Set();
    const source = new Map();
    for (const role of selectedRoles) {
      for (const key of role.permissions || []) {
        grants.add(key);
        if (!source.has(key)) source.set(key, role.name);
      }
    }
    return { roleGrants: grants, grantSource: source };
  }, [selectedRoles]);

  const toggleRole = (roleId) => {
    setSelectedRoleIds(prev => prev.includes(roleId)
      ? prev.filter(id => id !== roleId)
      : [...prev, roleId]);
  };

  const onOverrideChange = (key, state) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (state === INHERIT) delete next[key];
      else if (state === GRANT) next[key] = true;
      else if (state === REVOKE) next[key] = false;
      return next;
    });
  };

  const exceptionCount = Object.keys(overrides).length;

  const repOptions = [
    { value: null, label: 'Not linked to a rep' },
    ...reps.map(r => ({
      value: r.id,
      label: r.linked_user_id && r.linked_user_id !== user.id
        ? `${r.name} — already linked to ${r.linked_user_name}`
        : r.name,
      searchText: `${r.name} ${r.email || ''}`
    }))
  ];

  const suggestedRep = reps.find(r => r.suggested_user_id === user.id);

  return (
    <div className="user-access">
      <div className={`access-summary badge-${badge.toLowerCase()}`}>
        <span className={`status-badge badge-${badge.toLowerCase()}`}>
          {badge === 'ADMIN' ? 'Admin' : badge === 'REP' ? 'Rep' : 'User'}
        </span>
        <p>{BADGE_NOTE[badge]}</p>
      </div>

      <div className="field">
        <span className="field-label">Roles</span>
        {roles.length === 0 ? (
          <p className="field-note">No roles have been created yet.</p>
        ) : (
          <div className="role-list">
            {roles.map(role => (
              <div className="role-option" key={role.id}>
                <TickCheckbox
                  id={`role-${role.id}`}
                  checked={selectedRoleIds.includes(role.id)}
                  disabled={saving}
                  ariaLabel={role.name}
                  onChange={() => toggleRole(role.id)}
                />
                <label htmlFor={`role-${role.id}`} className="role-info">
                  <div className="role-name">
                    {role.name}
                    <span className={`status-badge badge-${role.badge.toLowerCase()}`}>
                      {role.badge === 'ADMIN' ? 'Admin' : role.badge === 'REP' ? 'Rep' : 'User'}
                    </span>
                    <span className="role-perm-count">{(role.permissions || []).length} permissions</span>
                  </div>
                  {role.description && <div className="role-description">{role.description}</div>}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="field">
        <span className="field-label">
          Sales rep {needsRep && <span className="field-required">required for a Rep role</span>}
        </span>
        <p className="field-note">
          A Rep account only ever sees the customers on this rep’s patch — their
          contacts, orders, invoices, purchase orders, standing orders and the
          transport plan for their loads.
        </p>
        <SearchableDropdown
          options={repOptions}
          value={repId}
          onChange={(value) => setRepId(value)}
          placeholder="Select a sales rep…"
          disabled={saving}
        />
        {suggestedRep && repId !== suggestedRep.id && (
          <button
            type="button"
            className="rep-suggestion"
            onClick={() => setRepId(suggestedRep.id)}
            disabled={saving}
          >
            <span className="material-symbols-rounded">auto_awesome</span>
            {suggestedRep.name} has the same email address — link them?
          </button>
        )}
        {needsRep && !repId && (
          <p className="field-error">
            Pick a sales rep before saving. A Rep account with no rep linked would
            match no customers at all and would open onto an empty system.
          </p>
        )}
      </div>

      <XeroConnectionField
        user={user}
        xero={xero}
        badge={badge}
        choice={xeroConnection}
        setChoice={setXeroConnection}
        saving={saving}
      />

      <div className="field">
        <span className="field-label">
          Exceptions
          {exceptionCount > 0 && <span className="field-count">{exceptionCount} for this user</span>}
        </span>
        <p className="field-note">
          Everything below comes from the roles above. Tick or untick a line to
          make an exception for this one person; tick it back to return it to
          whatever their roles say.
          {badge === 'ADMIN' && ' Admins hold everything regardless, so these have no effect until the Admin role is removed.'}
        </p>
        <PermissionPicker
          mode="tristate"
          categories={categories}
          permissions={permissions}
          overrides={overrides}
          roleGrants={roleGrants}
          roleNameFor={(key) => grantSource.get(key)}
          onOverrideChange={onOverrideChange}
          disabled={saving}
        />
      </div>
    </div>
  );
}

function UserAccessModal({
  isOpen, user, roles, categories, permissions, reps, xero = null, onClose, onSave, saving
}) {
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [repId, setRepId] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [xeroConnection, setXeroConnection] = useState('default');

  useEffect(() => {
    if (!isOpen || !user) return;
    setSelectedRoleIds((user.roles || []).map(r => r.id));
    setRepId(user.rep_id || null);
    setOverrides({ ...(user.overrides || {}) });
    setXeroConnection(user.xero_connection || 'default');
  }, [isOpen, user]);

  const selectedRoles = roles.filter(r => selectedRoleIds.includes(r.id));
  const badge = badgeFrom(selectedRoles);
  const repWithoutRep = badge === 'REP' && !repId;
  // A Rep badge only ever saves Default (the server refuses anything else).
  // Left as stored, it is reset to Default by the roles save itself, so only
  // someone choosing it — or able to choose — is asked to put it back.
  const storedXero = user?.xero_connection || 'default';
  const repWithXero = badge === 'REP' && xeroConnection !== 'default'
    && (xeroConnection !== storedXero || xero?.canChange === true);
  const blocked = repWithoutRep || repWithXero;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? `Access — ${user.first_name} ${user.last_name}`.trim() : 'Access'}
      size="lg"
      className="user-access-modal"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={() => onSave({ roleIds: selectedRoleIds, repId, overrides, xeroConnection })}
              disabled={saving || blocked}
              title={repWithoutRep
                ? 'Link a sales rep before saving a Rep role'
                : repWithXero ? 'Set the Xero connection back to Default — Rep accounts never use Xero' : undefined}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      {isOpen && user && (
        <UserAccessBody
          user={user}
          roles={roles}
          categories={categories}
          permissions={permissions}
          reps={reps}
          selectedRoleIds={selectedRoleIds}
          setSelectedRoleIds={setSelectedRoleIds}
          repId={repId}
          setRepId={setRepId}
          overrides={overrides}
          setOverrides={setOverrides}
          xero={xero}
          xeroConnection={xeroConnection}
          setXeroConnection={setXeroConnection}
          saving={saving}
        />
      )}
    </Modal>
  );
}

export default UserAccessModal;
